import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HiMenu, HiX, HiLogout, HiUser, HiCreditCard, HiHome, HiDocumentText, HiPhoneOutgoing, HiCollection, HiChatAlt2, HiLibrary, HiDocumentDuplicate, HiCog, HiCubeTransparent, HiMicrophone, HiClipboardList, HiGlobeAlt, HiChevronDoubleLeft, HiChevronDoubleRight, HiMoon, HiSun } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const NavBar = ({ isCollapsed = false, onToggleCollapse }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [mobileDropdownPosition, setMobileDropdownPosition] = useState('bottom');
  
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const userProfileRef = useRef(null);
  const mobileProfileRef = useRef(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target)) {
        setIsDesktopDropdownOpen(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setIsMobileDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine dropdown position
  useEffect(() => {
    if (isDesktopDropdownOpen && userProfileRef.current) {
      const rect = userProfileRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // If there's not enough space below, position above
      if (spaceBelow < 100) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isDesktopDropdownOpen]);

  // Determine mobile dropdown position
  useEffect(() => {
    if (isMobileDropdownOpen && mobileProfileRef.current) {
      const rect = mobileProfileRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      
      // If there's not enough space below, position above
      if (spaceBelow < 100) {
        setMobileDropdownPosition('top');
      } else {
        setMobileDropdownPosition('bottom');
      }
    }
  }, [isMobileDropdownOpen]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint
        setIsMobileMenuOpen(false);
        setIsMobileDropdownOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const Logo = ({ compact = false }) => (
    <div className={`flex items-center gap-3 min-w-0 ${compact ? 'justify-center' : 'flex-1 min-w-0'}`}>
      <div className="brand-mark flex items-center justify-center relative flex-shrink-0">
        <img 
          src="/logo.png" 
          alt="Dialog Logo" 
          className="h-8 w-8 object-contain"
        />
      </div>
      <div className={`flex flex-col min-w-0 transition-opacity duration-200 ${compact ? 'hidden' : ''}`}>
        <span className="text-sm md:text-base font-black tracking-tight text-slate-950 dark:text-slate-100 leading-tight whitespace-nowrap">
          Lifelike Agents
        </span>
        <span className="text-[9px] md:text-[10px] text-slate-500 dark:text-slate-400 tracking-[0.04em] uppercase leading-none whitespace-nowrap">
          AI conversation ops
        </span>
      </div>
    </div>
  );

  const navCategories = [
    {
      title: 'Main',
      items: [
        { id: 'dashboard', path: '/app', label: 'Dashboard', icon: <HiHome className="w-5 h-5" /> },
        { id: 'credits', path: '/app/credits', label: 'Credits', icon: <HiCreditCard className="w-5 h-5" /> },
      ]
    },
    {
      title: 'Calls',
      items: [
        { id: 'send-call', path: '/app/send-call', label: 'Send Call', icon: <HiPhoneOutgoing className="w-5 h-5" /> },
        { id: 'calls', path: '/app/calls', label: 'Calls History', icon: <HiCollection className="w-5 h-5" /> },
        { id: 'campaigns', path: '/app/campaigns', label: 'Campaigns', icon: <HiClipboardList className="w-5 h-5" /> },
      ]
    },
    {
      title: 'Dialog Flows',
      items: [
        { id: 'dialogFlow', path: '/app/dialog-flows', label: 'Dialog Flows', icon: <HiCubeTransparent className="w-5 h-5" /> },
        { id: 'chats', path: '/app/dialog-flows/chats', label: 'Chat with Dialog Flows', icon: <HiChatAlt2 className="w-5 h-5" /> },
      ]
    },
    {
      title: 'Resources',
      items: [
        { id: 'contacts', path: '/app/contacts', label: 'Contacts', icon: <HiUser className="w-5 h-5" /> },
        { id: 'knowledge-bases', path: '/app/knowledge-bases', label: 'Knowledge Bases', icon: <HiLibrary className="w-5 h-5" /> },
        { id: 'voices', path: '/app/voices', label: 'Voices', icon: <HiMicrophone className="w-5 h-5" /> },
        { id: 'phone-numbers', path: '/app/phone-numbers', label: 'Phone Numbers', icon: <HiPhoneOutgoing className="w-5 h-5" /> },
        { id: 'twilio-management', path: '/app/twilio-management', label: 'Twilio Management', icon: <HiPhoneOutgoing className="w-5 h-5" /> },
        { id: 'web-agents', path: '/app/web-agents', label: 'Web Agents', icon: <HiGlobeAlt className="w-5 h-5" /> },
        { id: 'custom-tools', path: '/app/custom-tools', label: 'Custom Tools', icon: <HiCog className="w-5 h-5" /> },
      ]
    },
    ...(isAdmin ? [{
      title: 'Admin',
      items: [
        { id: 'demo', path: '/app/demo', label: 'Demo Form', icon: <HiDocumentDuplicate className="w-5 h-5" /> },
        { id: 'admin', path: '/app/admin', label: 'Admin Dashboard', icon: <HiCog className="w-5 h-5" /> },
        { id: 'blog-admin', path: '/app/blog-admin', label: 'Blog Admin', icon: <HiDocumentText className="w-5 h-5" /> }
      ]
    }] : [])
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActivePath = (path) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    if (path === '/app/dialog-flows') {
      return location.pathname === path || /^\/app\/dialog-flows\/(?!chats(?:\/|$))/.test(location.pathname);
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-16 app-mobile-header z-50">
        <div className="flex items-center justify-between px-3 h-full">
          <Logo />
          <div className="flex items-center gap-1.5">
            <div 
              ref={mobileProfileRef}
              className="relative cursor-pointer p-1.5"
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileDropdownOpen(!isMobileDropdownOpen);
              }}
            >
              <div className="nav-avatar">
                <HiUser className="w-4 h-4" />
              </div>
              
              {/* User dropdown menu (mobile) */}
              {isMobileDropdownOpen && (
                <div 
                  ref={mobileDropdownRef}
                  className={`absolute z-50 app-popover w-52 divide-y divide-slate-100 dark:divide-slate-700/90 ${
                    mobileDropdownPosition === 'top' 
                      ? 'bottom-full right-0 mb-2' 
                      : 'top-full right-0 mt-2'
                  }`}
                >
                  <Link
                    to="/app/settings"
                    className="block px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/90 transition-colors duration-150"
                    onClick={() => {
                      setIsMobileDropdownOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <HiCog className="w-4 h-4 text-indigo-500" />
                      <span>Settings</span>
                    </div>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-b-lg transition-colors text-left"
                  >
                    <HiLogout className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="icon-button"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <HiX className="w-5 h-5" />
              ) : (
                <HiMenu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-slate-950/45 backdrop-blur-sm z-40" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Sidebar Navigation */}
      <nav className={`
        fixed top-0 left-0 h-full app-sidebar z-40 transition-all duration-300
        md:translate-x-0 md:z-30 flex flex-col
        ${isCollapsed ? 'md:w-20' : 'md:w-64'}
        w-64
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Desktop Logo - hidden on mobile */}
        <div className={`hidden md:flex items-center py-5 ${isCollapsed ? 'justify-center px-3' : 'justify-between px-5'}`}>
          <Logo compact={isCollapsed} />
          <button
            type="button"
            onClick={onToggleCollapse}
            className="icon-button sidebar-collapse-button"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <HiChevronDoubleRight className="w-4 h-4" />
            ) : (
              <HiChevronDoubleLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Mobile Logo - shown only when menu is open */}
        <div className="md:hidden h-16" />

        {/* Navigation Items */}
        <div className={`sidebar-nav-scroll pb-4 flex-grow overflow-y-auto ${isCollapsed ? 'md:px-2 md:space-y-2' : 'px-3 space-y-5'}`}>
          {navCategories.map((category, index) => (
            <div key={index} className="space-y-1.5">
              <h3 className={`px-3 text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] ${isCollapsed ? 'md:sr-only' : ''}`}>
                {category.title}
              </h3>
              {category.items.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  title={isCollapsed ? item.label : undefined}
                  className={`
                    nav-link block px-3 py-2.5 text-sm font-semibold
                    ${isCollapsed ? 'md:px-0 md:flex md:items-center md:justify-center' : ''}
                    ${isActivePath(item.path)
                      ? 'nav-link-active'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-950 dark:hover:text-slate-100'
                    }
                  `}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className={`flex items-center gap-2 ${isCollapsed ? 'md:justify-center' : ''}`}>
                    {item.icon}
                    <span className={isCollapsed ? 'md:sr-only' : ''}>{item.label}</span>
                  </div>
                </Link>
              ))}
            </div>
          ))}
        </div>

        <div className={`px-3 pt-1 pb-2 ${isCollapsed ? 'md:px-1.5' : ''}`}>
          <button
            type="button"
            className="icon-button w-full justify-center"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {resolvedTheme === 'dark' ? (
              <HiSun className="w-5 h-5" aria-hidden />
            ) : (
              <HiMoon className="w-5 h-5" aria-hidden />
            )}
          </button>
        </div>

        {/* User Profile Section */}
        <div className="border-t border-slate-200/80 dark:border-slate-700/80 p-3">
          <div className="relative">
            <div 
              ref={userProfileRef}
              className={`flex items-center gap-3 cursor-pointer hover:bg-slate-100/80 dark:hover:bg-slate-800/80 p-2 rounded-lg transition-colors ${isCollapsed ? 'md:justify-center' : ''}`}
              onClick={() => setIsDesktopDropdownOpen(!isDesktopDropdownOpen)}
              title={isCollapsed ? user?.email : undefined}
            >
              <div className="nav-avatar">
                <HiUser className="w-5 h-5" />
              </div>
              <div className={`flex-grow min-w-0 ${isCollapsed ? 'md:hidden' : ''}`}>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[165px]">{user?.email}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {isAdmin ? 'Administrator' : 'User'}
                </div>
              </div>
            </div>
            
            {/* User dropdown menu (desktop) */}
            {isDesktopDropdownOpen && (
              <div 
                ref={desktopDropdownRef}
                className={
                  isCollapsed
                    ? 'absolute left-full bottom-0 z-10 app-popover divide-y divide-slate-100 dark:divide-slate-700/90 ml-2 w-56'
                    : `absolute left-0 right-0 z-10 app-popover divide-y divide-slate-100 dark:divide-slate-700/90 ${
                        dropdownPosition === 'top' 
                          ? 'bottom-full mb-2' 
                          : 'top-full mt-2'
                      }`
                }
              >
                <Link
                  to="/app/settings"
                  className="block px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/90 transition-colors duration-150"
                  onClick={() => setIsDesktopDropdownOpen(false)}
                >
                  <div className="flex items-center gap-2">
                    <HiCog className="w-5 h-5 text-indigo-500" />
                    <span>Settings</span>
                  </div>
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsDesktopDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-b-lg transition-colors text-left"
                >
                  <HiLogout className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar; 
